import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes, faPlus, faArrowUp, faArrowDown, faTrashCan, faGripVertical,
  faClockRotateLeft, faFileWord, faFilePdf, faRotateRight,
} from "@fortawesome/free-solid-svg-icons";
import {
  getSet, editSet, addQuestion, editQuestion, deleteQuestion,
  reorderQuestions, getHistory, exportSet,
} from "./crossExamApi";
import "./crossexampanel.css";

/* ── constants / helpers ─────────────────────────────────────────────── */
const FOLLOW_UP_KEYS = [
  ["agrees", "Agrees"],
  ["denies", "Denies"],
  ["cannot_recall", "Cannot remember"],
  ["unexpected", "Unexpected answer"],
];
const SIDE_OPTIONS = [["defence", "Defence"], ["prosecution", "Prosecution"]];
const STATUS_OPTIONS = [["draft", "Draft"], ["reviewed", "Reviewed"], ["final", "Final"]];

const DEFAULT_CATEGORIES = [
  "Facts and timeline", "Contradictions and omissions", "Identification of the accused",
  "Documentary and electronic evidence", "Medical and forensic evidence",
  "Credibility and reliability", "Suggestions (putting the case)",
];
const DEFAULT_STRATEGIES = {
  establishing_fact: "Establishing a fact", contradiction: "Identifying a contradiction",
  omission: "Highlighting an omission", credibility: "Testing credibility",
  clarification: "Clarification", identification: "Testing identification",
  expert_scope: "Testing the scope of expert opinion", suggestion: "Suggestion (putting the case)",
};

const withOptions = (list, current) =>
  current && !list.includes(current) ? [current, ...list] : list;

const followUpValue = (q, key) =>
  (q.follow_ups || []).find((f) => f.if_answer === key)?.question || "";

const DEFAULT_FU_LABELS = {
  agrees: "If the witness agrees",
  denies: "If the witness denies",
  cannot_recall: "If the witness cannot remember",
  unexpected: "If the answer is unexpected",
};

/* Typing pannumbodhu — text-a apdiye vechukko (trim panna space type panna mudiyaadhu).
   Server-la vandha mathha fields (label…) ellam preserve aagum, order fixed. */
const buildFollowUps = (q, key, value) => {
  const byKey = {};
  (q.follow_ups || []).forEach((f) => { byKey[f.if_answer] = f; });
  byKey[key] = { ...(byKey[key] || { if_answer: key }), question: value };
  return FOLLOW_UP_KEYS.map(([k]) => byKey[k]).filter(Boolean);
};

/* Save-ku anuppum bodhu mattum — trim, empty entry remove, label fill.
   Reference payload: [{ if_answer, question, label }, …] */
const followUpsPayload = (q, labels = DEFAULT_FU_LABELS) =>
  (q.follow_ups || [])
    .map((f) => ({
      ...f,
      if_answer: f.if_answer,
      question: (f.question || "").trim(),
      label: f.label || labels[f.if_answer] || DEFAULT_FU_LABELS[f.if_answer],
    }))
    .filter((f) => f.question);

/* "2026-10-29T06:17:39.997+00:00" → { date: "29 Oct 2026", time: "11:47:39 am" }  (Indian time, IST) */
const IST = "Asia/Kolkata";
const fmtWhen = (iso) => {
  if (!iso) return { date: "", time: "" };
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { date: String(iso), time: "" };
  return {
    date: d.toLocaleDateString("en-IN", { timeZone: IST, day: "2-digit", month: "short", year: "numeric" }),
    time: d.toLocaleTimeString("en-IN", { timeZone: IST, hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true }),
  };
};

/* History event → padikka easy-a irukkura text */
const EVENT_LABELS = {
  created: "Question set created",
  set_updated: "Details updated",
  question_added: "Question added",
  question_edited: "Question edited",
  question_deleted: "Question deleted",
  questions_reordered: "Questions reordered",
};
const FIELD_LABELS = {
  case_name: "case name", case_number: "case number", witness_name: "witness",
  side: "side", objective: "objective", status: "status", title: "title",
  question: "question text", purpose: "purpose", category: "category", strategy: "strategy",
  leading: "leading", follow_ups: "follow-ups", advocate_note: "advocate's note", reviewed: "reviewed",
};
const describeHistory = (h, questionNo) => {
  const ev = h.event || h.action || h.type;
  const d = h.detail || {};
  let text = h.summary || EVENT_LABELS[ev] || (ev ? ev.replace(/_/g, " ") : "Edited");
  const qn = d.question_id && questionNo[d.question_id];
  if (ev === "question_edited" && qn) text = `Question ${qn} edited`;
  if (ev === "question_added" && Number.isInteger(d.position)) text = `Question ${d.position + 1} added`;
  if (ev === "question_deleted" && Number.isInteger(d.position)) text = `Question ${d.position + 1} deleted`;
  const fields = (d.fields || []).map((f) => FIELD_LABELS[f] || f.replace(/_/g, " "));
  return { text, fields: fields.join(", ") };
};

/* Auto-growing textarea (screenshot la irukkura chinna scroll box venaam) */
const AutoTextarea = ({ className = "", value, onChange, onBlur, placeholder, minRows = 2 }) => {
  const ref = useRef(null);
  const fit = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight + 2}px`;
  }, []);
  useEffect(() => { fit(); }, [value, fit]);
  useEffect(() => {
    // Panel resize / mobile width change aana height recalc
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    let lastW = el.clientWidth;
    let frame = 0;
    const ro = new ResizeObserver(() => {
      if (el.clientWidth === lastW) return;
      lastW = el.clientWidth;
      // Observer callback-kulla height maathuna "ResizeObserver loop" error varum →
      // adutha frame-la mattum resize pannu
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(fit);
    });
    ro.observe(el);
    return () => {
      cancelAnimationFrame(frame);
      ro.disconnect();
    };
  }, [fit]);
  return (
    <textarea
      ref={ref}
      rows={minRows}
      className={`cxp-input cxp-textarea ${className}`}
      value={value ?? ""}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
    />
  );
};

const Collapsible = ({ title, count, defaultOpen = false, children }) => (
  <details className="cxp-collapse" open={defaultOpen}>
    <summary>
      {title}
      {typeof count === "number" && <span className="cxp-collapse-count">({count})</span>}
    </summary>
    <div className="cxp-collapse-body">{children}</div>
  </details>
);

const SourceLine = ({ src }) => {
  const mark = src.mark || (src.verification?.status === "verified" ? "✓" : "⚠");
  const tone = mark === "✓" ? "ok" : mark === "≈" ? "near" : "bad";
  return (
    <div className="cxp-source">
      <span className={`cxp-mark cxp-mark-${tone}`}>{mark}</span>{" "}
      <span className="cxp-source-doc">
        {src.display || `${src.filename || src.label}${src.page_no ? `, p. ${src.page_no}` : ""}`}
      </span>
      {src.quote && <span className="cxp-source-quote"> — “{src.quote}”</span>}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════
   Main panel
   ═══════════════════════════════════════════════════════════════════════ */
const CrossExamPanel = ({
  userId,
  setId,
  onClose,
  width = 520,
  isResizing = false,
  onResizeStart,
  notify = () => {},           // (type, message) → toast
}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [pending, setPending] = useState(0);
  const [lastError, setLastError] = useState("");
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [exporting, setExporting] = useState("");
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const [handleArmedId, setHandleArmedId] = useState(null);
  const [flashId, setFlashId] = useState(null);

  // Last server-confirmed values → blur la change illana PATCH anuppa vendaam
  const savedRef = useRef({ meta: {}, questions: {} });
  const bodyRef = useRef(null);
  const dataRef = useRef(null);
  useEffect(() => { dataRef.current = data; }, [data]);

  const snapshot = useCallback((set) => {
    if (!set) return;
    savedRef.current.meta = {
      case_name: set.case?.name ?? "",
      case_number: set.case?.number ?? "",
      witness_name: set.witness?.name ?? "",
      side: set.side ?? "",
      objective: set.objective ?? "",
      status: set.status ?? "",
      title: set.title ?? "",
    };
    const qs = {};
    (set.questions || []).forEach((q) => { qs[q.id] = JSON.parse(JSON.stringify(q)); });
    savedRef.current.questions = qs;
  }, []);

  /* ── load ──────────────────────────────────────────────────────────── */
  const load = useCallback(async () => {
    if (!userId || !setId) return;
    setLoading(true);
    setLoadError("");
    try {
      const set = await getSet(userId, setId);
      setData(set);
      snapshot(set);
    } catch (e) {
      setLoadError(e.message || "Could not load the question set.");
    } finally {
      setLoading(false);
    }
  }, [userId, setId, snapshot]);

  useEffect(() => {
    setData(null);
    setHistory(null);
    setHistoryOpen(false);
    load();
  }, [load]);

  /* ── generic mutation runner ───────────────────────────────────────── */
  // Ella save-um oru queue-la → ore neram rendu request same expected_version-oda pogaadhu
  const queueRef = useRef(Promise.resolve());

  // dataRef-ayum udane update pannu (adutha queued task render-ku munnadiye correct version paakanum)
  const applyVersion = useCallback((v) => {
    if (!Number.isInteger(v)) return;
    dataRef.current = dataRef.current && { ...dataRef.current, version: v };
    setData((prev) => prev && ({ ...prev, version: v }));
  }, []);

  /* fn(expectedVersion, freshSetOnRetry) → Promise<response> */
  const run = useCallback((fn) => {
    const task = async () => {
      setPending((n) => n + 1);
      setLastError("");
      try {
        let res;
        try {
          res = await fn(dataRef.current?.version);
        } catch (e) {
          // 409/412 = version mismatch → latest version eduthu oru thadava retry
          if (e.status !== 409 && e.status !== 412) throw e;
          const fresh = await getSet(userId, setId);
          applyVersion(fresh.version);
          res = await fn(fresh.version, fresh);
        }

        if (res && Array.isArray(res.questions)) {
          // Full set vandhuchu → adhaye use pannu
          dataRef.current = res;
          setData(res);
          snapshot(res);
        } else if (res && typeof res === "object" && (res.question?.id || res.id?.startsWith?.("q_"))) {
          const q = res.question || res;
          const nextV = Number.isInteger(res.set_version) ? res.set_version
            : Number.isInteger(res.version) && res.question ? res.version
            : (dataRef.current?.version ?? 0) + 1;       // server ovvoru save-kum +1 pannudhu
          setData((prev) => prev && ({
            ...prev,
            questions: prev.questions.map((x) => (x.id === q.id ? { ...x, ...q } : x)),
          }));
          applyVersion(nextV);
          savedRef.current.questions[q.id] = { ...(savedRef.current.questions[q.id] || {}), ...q };
        } else if (res && Number.isInteger(res.version)) {
          applyVersion(res.version);
        } else {
          // Response-la version illa (e.g. DELETE 204) → latest-a GET pannu
          const fresh = await getSet(userId, setId);
          dataRef.current = fresh;
          setData(fresh);
          snapshot(fresh);
        }
        setHistory(null); // stale — reopen pannumbodhu refetch
        return res;
      } catch (e) {
        setLastError(e.message || "Save failed");
        notify("error", e.message || "Save failed");
        throw e;
      } finally {
        setPending((n) => n - 1);
      }
    };
    const p = queueRef.current.then(task, task);
    queueRef.current = p.catch(() => {});
    return p;
  }, [userId, setId, snapshot, notify, applyVersion]);

  /* ── set-level (meta) edits ────────────────────────────────────────── */
  const setLocal = (patch) => setData((prev) => ({ ...prev, ...patch }));

  // Edit Set body flat: { case_name, case_number, witness_name, side, objective, status, expected_version }
  const metaPatch = (key, value) => {
    switch (key) {
      case "case_name":
      case "case_number":
        return { [key]: (value || "").trim() || null };
      default:
        return { [key]: value };
    }
  };

  const commitMeta = (key, value) => {
    if ((savedRef.current.meta[key] ?? "") === (value ?? "")) return;
    savedRef.current.meta[key] = value ?? "";
    run((v) => editSet(userId, setId, metaPatch(key, value), v)).catch(() => load());
  };

  /* ── question-level edits ──────────────────────────────────────────── */
  const updateQLocal = (qid, patch) =>
    setData((prev) => ({
      ...prev,
      questions: prev.questions.map((q) => (q.id === qid ? { ...q, ...patch } : q)),
    }));

  const commitQ = (qid, patch) => {
    const saved = savedRef.current.questions[qid] || {};
    const changed = Object.keys(patch).some(
      (k) => JSON.stringify(saved[k] ?? null) !== JSON.stringify(patch[k] ?? null)
    );
    if (!changed) return;
    savedRef.current.questions[qid] = { ...saved, ...patch };
    run((v) => editQuestion(userId, setId, qid, patch, v)).catch(() => load());
  };

  // select / checkbox — udane save
  const setQNow = (qid, patch) => { updateQLocal(qid, patch); commitQ(qid, patch); };

  /* ── add / delete / reorder ────────────────────────────────────────── */
  const handleAdd = async () => {
    const before = new Set((data?.questions || []).map((q) => q.id));
    try {
      await run((v) => addQuestion(userId, setId, {
        category: "Facts and timeline",
        question: "New question",
        purpose: "",
        strategy: "clarification",
        leading: false,
      }, v));
      // pudhusa vandha question-ku scroll
      setTimeout(() => {
        const added = (dataRef.current?.questions || []).find((q) => !before.has(q.id));
        if (added) {
          setFlashId(added.id);
          document.getElementById(`cxp-q-${added.id}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
          setTimeout(() => setFlashId(null), 1600);
        }
      }, 80);
    } catch (_) { /* notified */ }
  };

  const handleDelete = async (qid) => {
    setConfirmDeleteId(null);
    const prev = data;
    setData((d) => ({ ...d, questions: d.questions.filter((q) => q.id !== qid) }));
    try {
      await run((v) => deleteQuestion(userId, setId, qid, v));
    } catch (_) {
      setData(prev);
    }
  };

  const applyOrder = async (ids) => {
    const prev = data;
    const byId = Object.fromEntries(data.questions.map((q) => [q.id, q]));
    setData((d) => ({ ...d, questions: ids.map((id) => byId[id]).filter(Boolean) }));
    try {
      await run((v, fresh) => {
        if (!fresh) return reorderQuestions(userId, setId, ids, v);
        // Retry: server-la ippo irukkura ids-oda match pannu (vera tab-la add/delete aagirukkalaam)
        const freshIds = new Set((fresh.questions || []).map((q) => q.id));
        const keep = ids.filter((id) => freshIds.has(id));
        const extra = [...freshIds].filter((id) => !keep.includes(id));
        return reorderQuestions(userId, setId, [...keep, ...extra], v);
      });
    } catch (_) {
      setData(prev);
    }
  };

  const move = (index, dir) => {
    const ids = data.questions.map((q) => q.id);
    const to = index + dir;
    if (to < 0 || to >= ids.length) return;
    [ids[index], ids[to]] = [ids[to], ids[index]];
    applyOrder(ids);
  };

  const onDrop = (targetId) => {
    if (!dragId || dragId === targetId) return;
    const ids = data.questions.map((q) => q.id).filter((id) => id !== dragId);
    const at = ids.indexOf(targetId);
    const fromIdx = data.questions.findIndex((q) => q.id === dragId);
    const targetIdx = data.questions.findIndex((q) => q.id === targetId);
    ids.splice(fromIdx < targetIdx ? at + 1 : at, 0, dragId);
    applyOrder(ids);
  };

  /* ── history / export ──────────────────────────────────────────────── */
  const toggleHistory = () => setHistoryOpen((o) => !o);

  // History open-a irundhu, data illana (first open / save-ku appuram stale) → fetch
  useEffect(() => {
    if (!historyOpen || history !== null) return undefined;
    let cancelled = false;
    setHistoryLoading(true);
    getHistory(userId, setId)
      .then((h) => {
        if (cancelled) return;
        setHistory(Array.isArray(h) ? h : h?.history || h?.items || h?.versions || []);
        setHistoryLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        notify("error", e.message || "Could not load history");
        setHistory([]);
        setHistoryLoading(false);
      });
    return () => { cancelled = true; setHistoryLoading(false); };
  }, [historyOpen, history, userId, setId, notify]);

  const handleExport = async (kind) => {
    if (pending > 0) {
      notify("info", "Saving changes… try again in a moment.");
      return;
    }
    setExporting(kind);
    try {
      await exportSet(userId, setId, kind, data?.title || "cross-examination");
    } catch (e) {
      notify("error", e.message || "Export failed");
    } finally {
      setExporting("");
    }
  };

  /* ── derived ───────────────────────────────────────────────────────── */
  const categories = data?.categories?.length ? data.categories : DEFAULT_CATEGORIES;
  const strategies = data?.strategies && Object.keys(data.strategies).length ? data.strategies : DEFAULT_STRATEGIES;
  const questions = data?.questions || [];
  // question_id → ippo irukkura number (history-la "Question 3 edited" kaatta)
  const questionNo = useMemo(
    () => Object.fromEntries((data?.questions || []).map((q, i) => [q.id, i + 1])),
    [data]
  );
  const missingItems = useMemo(() => ([
    ...(data?.missing_documents || []).map((m) => ({ strong: m.document, text: m.why_needed })),
    ...(data?.insufficient_information || []).map((t) => ({ text: t })),
  ]), [data]);

  const saveLabel = pending > 0 ? "Saving…" : lastError ? "Not saved" : data ? "Saved" : "";

  /* ═════════════════════════════ render ═════════════════════════════ */
  return (
    <div className={`doc-side-panel cxp-panel${isResizing ? " resizing" : ""}`} style={{ width: `${width}px` }}>
      {onResizeStart && (
        <div
          className="doc-resize-handle"
          onPointerDown={onResizeStart}
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize question editor"
        >
          <div className="doc-resize-grip"><span /><span /><span /><span /></div>
        </div>
      )}

      {/* ── Header ── */}
      <div className="cxp-header">
        <h6 className="cxp-title" title={data?.title}>{data?.title || "Cross-examination"}</h6>
        <div className="cxp-header-actions">
          <button type="button" className="cxp-btn cxp-btn-primary" onClick={handleAdd} disabled={!data}>
            <FontAwesomeIcon icon={faPlus} /> Question
          </button>
          <button type="button" className="cxp-btn" onClick={() => handleExport("word")} disabled={!data || !!exporting}>
            <FontAwesomeIcon icon={faFileWord} /> {exporting === "word" ? "…" : "Word"}
          </button>
          <button type="button" className="cxp-btn" onClick={() => handleExport("pdf")} disabled={!data || !!exporting}>
            <FontAwesomeIcon icon={faFilePdf} /> {exporting === "pdf" ? "…" : "PDF"}
          </button>
          <button
            type="button"
            className={`cxp-btn cxp-btn-icon${historyOpen ? " active" : ""}`}
            onClick={toggleHistory}
            disabled={!data}
            title="Version history"
            aria-label="Version history"
          >
            <FontAwesomeIcon icon={faClockRotateLeft} />
          </button>
          <button type="button" className="cxp-btn cxp-btn-icon" onClick={onClose} aria-label="Close">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      </div>
      {data && (
        <div className={`cxp-savebar${lastError ? " error" : ""}`}>
          <span>{saveLabel}{data.version ? ` · v${data.version}` : ""}</span>
          {lastError && (
            <button type="button" className="cxp-link" onClick={load}>
              <FontAwesomeIcon icon={faRotateRight} /> Reload
            </button>
          )}
        </div>
      )}

      <div className="cxp-body" ref={bodyRef}>
        {loading && <div className="cxp-state">Loading question set…</div>}
        {!loading && loadError && (
          <div className="cxp-state cxp-state-error">
            {loadError}
            <button type="button" className="cxp-btn" onClick={load}>Try again</button>
          </div>
        )}

        {!loading && data && (
          <>
            {/* ── History drawer ── */}
            {historyOpen && (
              <div className="cxp-history">
                <div className="cxp-label">Version history</div>
                {historyLoading && <div className="cxp-muted">Loading…</div>}
                {!historyLoading && history?.length === 0 && <div className="cxp-muted">No changes recorded yet.</div>}
                {!historyLoading && history?.length > 0 && (
                  <ul className="cxp-history-list">
                    {[...history].reverse().map((h, i) => {
                      const { text, fields } = describeHistory(h, questionNo);
                      const when = fmtWhen(h.ts || h.at || h.created_at || h.updated_at || h.timestamp);
                      const who = h.actor === "assistant" ? "JuristBot" : h.actor === "user" ? "You" : h.actor;
                      return (
                        <li key={h.entry_id || h.id || `${h.version}-${i}`}>
                          <span className="cxp-history-ver">v{h.version ?? "—"}</span>
                          <span className="cxp-history-what">
                            {text}
                            {fields && <span className="cxp-muted"> · {fields}</span>}
                            {who && <span className="cxp-history-who">by {who}</span>}
                          </span>
                          <span className="cxp-history-when">
                            <span>{when.date}</span>
                            <span className="cxp-muted">{when.time}</span>
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}

            {/* ── Set details ── */}
            <div className="cxp-grid">
              <label className="cxp-field">
                <span className="cxp-label">Case name</span>
                <input
                  className="cxp-input"
                  value={data.case?.name ?? ""}
                  onChange={(e) => setLocal({ case: { ...(data.case || {}), name: e.target.value } })}
                  onBlur={(e) => commitMeta("case_name", e.target.value)}
                />
              </label>
              <label className="cxp-field">
                <span className="cxp-label">Case number</span>
                <input
                  className="cxp-input"
                  value={data.case?.number ?? ""}
                  onChange={(e) => setLocal({ case: { ...(data.case || {}), number: e.target.value } })}
                  onBlur={(e) => commitMeta("case_number", e.target.value)}
                />
              </label>
              <label className="cxp-field">
                <span className="cxp-label">Witness</span>
                <input
                  className="cxp-input"
                  value={data.witness?.name ?? ""}
                  onChange={(e) => setLocal({ witness: { ...(data.witness || {}), name: e.target.value } })}
                  onBlur={(e) => commitMeta("witness_name", e.target.value)}
                />
              </label>
              <label className="cxp-field">
                <span className="cxp-label">Side</span>
                <select
                  className="cxp-input"
                  value={data.side || ""}
                  onChange={(e) => { setLocal({ side: e.target.value }); commitMeta("side", e.target.value); }}
                >
                  {SIDE_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </label>
              <label className="cxp-field cxp-span-2">
                <span className="cxp-label">Objective</span>
                <AutoTextarea
                  minRows={1}
                  value={data.objective}
                  onChange={(v) => setLocal({ objective: v })}
                  onBlur={(e) => commitMeta("objective", e.target.value)}
                />
              </label>
              <label className="cxp-field">
                <span className="cxp-label">Status</span>
                <select
                  className="cxp-input"
                  value={data.status || "draft"}
                  onChange={(e) => { setLocal({ status: e.target.value }); commitMeta("status", e.target.value); }}
                >
                  {withOptions(STATUS_OPTIONS.map(([v]) => v), data.status).map((v) => (
                    <option key={v} value={v}>
                      {(STATUS_OPTIONS.find(([k]) => k === v) || [v, v])[1]}
                    </option>
                  ))}
                </select>
              </label>
              <div className="cxp-field">
                <span className="cxp-label">Version</span>
                <div className="cxp-input cxp-readonly">v{data.version ?? 1}</div>
              </div>
            </div>

            {data.disclaimer && (
              <div className="cxp-disclaimer">
                <strong>Advocate review required.</strong>{" "}
                {data.disclaimer.replace(/^Advocate review required\.\s*/i, "")}
              </div>
            )}

            {/* ── Analysis ── */}
            <Collapsible title="Contradictions" count={(data.contradictions || []).length}>
              {(data.contradictions || []).length === 0 && <div className="cxp-muted">None found in the records.</div>}
              {(data.contradictions || []).map((c, i) => (
                <div className="cxp-analysis-item" key={c.id || i}>
                  <div className="cxp-analysis-head">
                    {c.topic}
                    <span className={`cxp-pill ${c.status === "confirmed" ? "cxp-pill-strong" : ""}`}>{c.status}</span>
                  </div>
                  {(c.versions || []).map((v, vi) => (
                    <div className="cxp-version" key={vi}>
                      <div>{v.says}</div>
                      {v.source && <SourceLine src={v.source} />}
                    </div>
                  ))}
                  {c.significance && <div className="cxp-significance">{c.significance}</div>}
                </div>
              ))}
            </Collapsible>

            <Collapsible title="Omissions" count={(data.omissions || []).length}>
              {(data.omissions || []).length === 0 && <div className="cxp-muted">None found in the records.</div>}
              {(data.omissions || []).map((o, i) => (
                <div className="cxp-analysis-item" key={o.id || i}>
                  <div className="cxp-analysis-head">{o.fact}</div>
                  <div className="cxp-muted">
                    Missing from: {o.absent_from}{o.present_in ? ` · Appears in: ${o.present_in}` : ""}
                  </div>
                  {o.significance && <div className="cxp-significance">{o.significance}</div>}
                </div>
              ))}
            </Collapsible>

            <Collapsible title="Missing documents and information" defaultOpen={missingItems.length > 0}>
              {missingItems.length === 0 && <div className="cxp-muted">Nothing flagged.</div>}
              <ul className="cxp-bullets">
                {missingItems.map((m, i) => (
                  <li key={i}>{m.strong && <strong>{m.strong}</strong>}{m.strong ? " — " : ""}{m.text}</li>
                ))}
              </ul>
            </Collapsible>

            {(data.records || []).length > 0 && (
              <div className="cxp-records">
                Records: {data.records.map((r) => `${r.label} ${r.filename}`).join(" · ")}
              </div>
            )}

            {/* ── Questions ── */}
            <div className="cxp-questions-head">
              Questions ({questions.length}) — drag <FontAwesomeIcon icon={faGripVertical} /> or use ↑ ↓ to reorder
            </div>

            {questions.length === 0 && (
              <div className="cxp-state">
                No questions yet.
                <button type="button" className="cxp-btn cxp-btn-primary" onClick={handleAdd}>
                  <FontAwesomeIcon icon={faPlus} /> Add a question
                </button>
              </div>
            )}

            {questions.map((q, index) => {
              const isAdvocate = q.origin === "advocate";
              return (
                <div
                  key={q.id}
                  id={`cxp-q-${q.id}`}
                  className={[
                    "cxp-qcard",
                    dragId === q.id ? "dragging" : "",
                    dragOverId === q.id && dragId !== q.id ? "drag-over" : "",
                    flashId === q.id ? "flash" : "",
                  ].join(" ")}
                  draggable={handleArmedId === q.id}
                  onDragStart={(e) => {
                    setDragId(q.id);
                    e.dataTransfer.effectAllowed = "move";
                    e.dataTransfer.setData("text/plain", q.id);
                  }}
                  onDragOver={(e) => { if (dragId) { e.preventDefault(); setDragOverId(q.id); } }}
                  onDragLeave={() => setDragOverId((cur) => (cur === q.id ? null : cur))}
                  onDrop={(e) => { e.preventDefault(); onDrop(q.id); setDragOverId(null); }}
                  onDragEnd={() => { setDragId(null); setDragOverId(null); setHandleArmedId(null); }}
                >
                  {/* row: handle · number · category · ↑ ↓ 🗑 */}
                  <div className="cxp-qtop">
                    <span
                      className="cxp-grip"
                      title="Drag to reorder"
                      onMouseDown={() => setHandleArmedId(q.id)}
                      onMouseUp={() => setHandleArmedId(null)}
                      onTouchStart={() => setHandleArmedId(q.id)}
                    >
                      <FontAwesomeIcon icon={faGripVertical} />
                    </span>
                    <span className="cxp-qnum">{index + 1}.</span>
                    <select
                      className="cxp-input cxp-category"
                      value={q.category || ""}
                      onChange={(e) => setQNow(q.id, { category: e.target.value })}
                    >
                      {withOptions(categories, q.category).map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <button type="button" className="cxp-icon-btn" onClick={() => move(index, -1)} disabled={index === 0} aria-label="Move up">
                      <FontAwesomeIcon icon={faArrowUp} />
                    </button>
                    <button type="button" className="cxp-icon-btn" onClick={() => move(index, 1)} disabled={index === questions.length - 1} aria-label="Move down">
                      <FontAwesomeIcon icon={faArrowDown} />
                    </button>
                    <button type="button" className="cxp-icon-btn cxp-danger" onClick={() => setConfirmDeleteId(q.id)} aria-label="Delete question">
                      <FontAwesomeIcon icon={faTrashCan} />
                    </button>
                  </div>

                  {confirmDeleteId === q.id && (
                    <div className="cxp-confirm">
                      Delete question {index + 1}?
                      <button type="button" className="cxp-btn cxp-btn-danger" onClick={() => handleDelete(q.id)}>Delete</button>
                      <button type="button" className="cxp-btn" onClick={() => setConfirmDeleteId(null)}>Cancel</button>
                    </div>
                  )}

                  <AutoTextarea
                    className="cxp-qtext"
                    value={q.question}
                    onChange={(v) => updateQLocal(q.id, { question: v })}
                    onBlur={(e) => commitQ(q.id, { question: e.target.value })}
                  />

                  <div className="cxp-label">Purpose</div>
                  <AutoTextarea
                    minRows={1}
                    value={q.purpose}
                    placeholder="Why this question is asked"
                    onChange={(v) => updateQLocal(q.id, { purpose: v })}
                    onBlur={(e) => commitQ(q.id, { purpose: e.target.value })}
                  />

                  <div className="cxp-row">
                    <select
                      className="cxp-input cxp-strategy"
                      value={q.strategy || ""}
                      onChange={(e) => setQNow(q.id, { strategy: e.target.value })}
                    >
                      {withOptions(Object.keys(strategies), q.strategy).map((k) => (
                        <option key={k} value={k}>{strategies[k] || q.strategy_label || k}</option>
                      ))}
                    </select>
                    <label className="cxp-check">
                      <input
                        type="checkbox"
                        checked={!!q.leading}
                        onChange={(e) => setQNow(q.id, { leading: e.target.checked })}
                      />
                      Leading question
                    </label>
                  </div>

                  <div className="cxp-label">Source</div>
                  {(q.sources || []).length > 0
                    ? q.sources.map((s, si) => <SourceLine key={si} src={s} />)
                    : <div className="cxp-source-plain">{q.based_on_instructions || isAdvocate ? "Advocate's instructions" : "—"}</div>}

                  {q.caution && <div className="cxp-flag">⚠ {q.caution}</div>}
                  {(q.flags || []).map((f, fi) => (
                    <div className="cxp-flag" key={f.code || fi}>⚠ {f.message}</div>
                  ))}

                  {(q.requires_verification || q.verification_note) && (
                    <div className="cxp-verify">
                      🔎 {q.verification_note || "Verify before use."}
                      {(q.needs_documents || []).length > 0 && (
                        <> — <strong>needs:</strong> {q.needs_documents.join(", ")}</>
                      )}
                    </div>
                  )}

                  <div className="cxp-label">Suggested follow-up</div>
                  <div className="cxp-followups">
                    {FOLLOW_UP_KEYS.map(([key, label]) => (
                      <React.Fragment key={key}>
                        <span className="cxp-fu-label">{label}</span>
                        <input
                          className="cxp-input"
                          value={followUpValue(q, key)}
                          placeholder="—"
                          title={followUpValue(q, key)}
                          onChange={(e) => updateQLocal(q.id, { follow_ups: buildFollowUps(q, key, e.target.value) })}
                          onBlur={() => {
                            const follow_ups = followUpsPayload(q, data.follow_up_labels);
                            updateQLocal(q.id, { follow_ups });      // trimmed version-a local-layum vai
                            commitQ(q.id, { follow_ups });
                          }}
                        />
                      </React.Fragment>
                    ))}
                  </div>

                  <div className="cxp-label">Advocate's note</div>
                  <input
                    className="cxp-input"
                    value={q.advocate_note || ""}
                    placeholder="Private note (exported)"
                    onChange={(e) => updateQLocal(q.id, { advocate_note: e.target.value })}
                    onBlur={(e) => commitQ(q.id, { advocate_note: e.target.value || null })}
                  />

                  <div className="cxp-qfoot">
                    <label className="cxp-check">
                      <input
                        type="checkbox"
                        checked={!!q.reviewed}
                        onChange={(e) => setQNow(q.id, { reviewed: e.target.checked })}
                      />
                      Reviewed by advocate
                    </label>
                    <span className="cxp-muted">{isAdvocate ? "Added by you" : "Drafted by JuristBot"}</span>
                  </div>
                </div>
              );
            })}

            {(data.sequencing_note || (data.statutory_notes || []).length > 0) && (
              <div className="cxp-notes">
                {data.sequencing_note && (
                  <p><strong>Sequence:</strong> {data.sequencing_note.replace(/"\s*$/, "")}</p>
                )}
                {(data.statutory_notes || []).length > 0 && (
                  <p><strong>Statutory references (verify before relying on them):</strong> {data.statutory_notes.join(" ")}</p>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CrossExamPanel;